/* eslint-disable prettier/prettier */
import { InfoOutlined, Search, Visibility } from '@mui/icons-material'
import { Autocomplete, Box, Button, Card, TextField, Tooltip, Typography, autocompleteClasses } from '@mui/material'
import React, { SyntheticEvent, useEffect, useState } from 'react'
import { fullName } from '../../domain/Account/AccountDisplay'
import { AccountDtoRoles } from '../../interactors/gen/backendClient'
import { useAccountsStore } from '../../store/AccountsStore'
import { useAdoptersStore } from '../../store/AdoptersStore'
import { useFetchOrRefreshAdoptersOnMount } from '../../store/useFetchOrRefreshAdoptersOnMount'
import { useFetchOrRefreshMembersOnMount } from '../../store/useFetchOrRefreshMembersOnMount'
import { FilterList } from '../common/FilterList'
import { FilterListItem } from '../common/FilterListItem'
import { OverviewFilterDialog, OverviewFilterSection } from '../common/OverviewFilterDialog'
import { overviewActionButtonSx, overviewFilterCountBadgeSx, overviewResetFiltersSx } from '../common/overviewStyles'
import { searchFieldSx } from '../common/searchFieldSx'
import { spacingItem, theme } from '../theme'
import { MapCoordinates, MapOfFrance } from './MapOfFrance'

export const MapScreen: React.FC = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [memberMarkers, setMemberMarkers] = useState<MapCoordinates[]>([])
  const [hostFamilyMarkers, setHostFamilyMarkers] = useState<MapCoordinates[]>([])
  const [veterinaryMarkers, setVeterinaryMarkers] = useState<MapCoordinates[]>([])
  const [adopterMarkers, setAdopterMarkers] = useState<MapCoordinates[]>([])

  const [markAdopters, setMarkAdopters] = useState<boolean>(true)
  const [markMembers, setMarkMembers] = useState<boolean>(true)
  const [markHostFamily, setMarkHostFamily] = useState<boolean>(true)
  const [markVeterinary, setMarkVeterinary] = useState<boolean>(true)
  const activeFiltersCount = [!markMembers, !markHostFamily, !markVeterinary, !markAdopters].filter(Boolean).length

  const [selectedMarker, setSelectedMarker] = useState<MapCoordinates | null>(
    queryParams.get('selectedMarker') != null ? JSON.parse(queryParams.get('selectedMarker')!) : null
  )
  const resetFilters = () => {
    setMarkMembers(true)
    setMarkHostFamily(true)
    setMarkVeterinary(true)
    setMarkAdopters(true)
  }

  useFetchOrRefreshAdoptersOnMount()
  useFetchOrRefreshMembersOnMount()

  const accountsStore = useAccountsStore()
  const adoptersStore = useAdoptersStore()

  const positionToExclude = [46.227638, 2.213749] // France's coordinates

  useEffect(() => {
    // Create new arrays for each marker type
    const newMemberMarkers = accountsStore.members
      .filter(
        (member) =>
          member.roles.includes(AccountDtoRoles.Member) &&
          member.coordinates &&
          member.isDeleted !== true &&
          member.coordinates.lat !== positionToExclude[0] &&
          member.coordinates.lng !== positionToExclude[1]
      )
      .map((account) => ({
        position: [account.coordinates?.lat, account.coordinates?.lng],
        title: `${fullName(account)} - Membre`,
        id: account.id,
        role: AccountDtoRoles.Member,
        address: account.address,
        postalCode: account.postalCode,
        city: account.city,
        country: account.country,
      }))

    const newHostFamilyMarkers = accountsStore.members
      .filter(
        (member) =>
          member.roles.includes(AccountDtoRoles.HostFamily) &&
          member.coordinates &&
          member.isDeleted !== true &&
          member.coordinates.lat !== positionToExclude[0] &&
          member.coordinates.lng !== positionToExclude[1]
      )
      .map((account) => ({
        position: [account.coordinates?.lat, account.coordinates?.lng],
        title: `${fullName(account)} - Famille d'accueil`,
        id: account.id,
        role: AccountDtoRoles.HostFamily,
        address: account.address,
        postalCode: account.postalCode,
        city: account.city,
        country: account.country,
      }))

    const newVeterinaryMarkers = accountsStore.members
      .filter(
        (member) =>
          member.roles.includes(AccountDtoRoles.Veterinary) &&
          member.coordinates &&
          member.isDeleted !== true &&
          member.coordinates.lat !== positionToExclude[0] &&
          member.coordinates.lng !== positionToExclude[1]
      )
      .map((account) => ({
        position: [account.coordinates?.lat, account.coordinates?.lng],
        title: `${fullName(account)} - Vétérinaire`,
        id: account.id,
        role: AccountDtoRoles.Veterinary,
        address: account.address,
        postalCode: account.postalCode,
        city: account.city,
        country: account.country,
      }))

    const newAdopterMarkers = adoptersStore.adopters
      .filter(
        (adopter) =>
          accountsStore.isAdmin() &&
          adopter.coordinates &&
          adopter.coordinates.lat !== positionToExclude[0] &&
          adopter.coordinates.lng !== positionToExclude[1]
      )
      .map((adopter) => ({
        position: [adopter.coordinates?.lat, adopter.coordinates?.lng],
        title: `${fullName(adopter)} - Adoptant`,
        id: adopter.id,
        address: adopter.address,
        postalCode: adopter.postalCode,
        city: adopter.city,
        country: adopter.country,
      }))

    // Set the new markers state
    setMemberMarkers(newMemberMarkers as MapCoordinates[])
    setHostFamilyMarkers(newHostFamilyMarkers as MapCoordinates[])
    setVeterinaryMarkers(newVeterinaryMarkers as MapCoordinates[])
    setAdopterMarkers(newAdopterMarkers as MapCoordinates[])
  }, [markAdopters, markMembers, markHostFamily, markVeterinary, accountsStore.members, adoptersStore.adopters])

  const handleSelectMarker = (_event: SyntheticEvent<Element, Event>, value: MapCoordinates | null) => {
    setQueryParams({ selectedMarker: value ? JSON.stringify(value) : null })
    setSelectedMarker(value)
  }

  const setQueryParams = (params: { [key: string]: string | null }) => {
    const newParams = new URLSearchParams(window.location.search)
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value)
      } else {
        newParams.delete(key)
      }
    })
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`)
  }

  const FiltersBar = () => {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <OverviewFilterSection>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">
                {`Total:
                ${
                  [
                    ...(markMembers ? memberMarkers : []),
                    ...(markHostFamily ? hostFamilyMarkers : []),
                    ...(markVeterinary ? veterinaryMarkers : []),
                    ...(markAdopters ? adopterMarkers : []),
                  ].length
                }/${
                  [
                    ...accountsStore.members.filter((member) => member.isDeleted !== true),
                    ...(accountsStore.isAdmin() ? adoptersStore.adopters : []),
                  ].length
                }`}
              </Typography>
              <Tooltip
                title={`Cette carte tient compte des préférences des utilisateurs et ne fait apparaitre que ceux qui le souhaitent (onglet "visibilité"). De plus ceux ayant une adresse incomplète n'apparaîtront pas non plus.`}
              >
                <InfoOutlined sx={{ color: theme.palette.primary.main }} />
              </Tooltip>
            </Box>
            <FilterList label="Afficher" icon={<Visibility />}>
              <FilterListItem
                label="Membres"
                isSelected={markMembers}
                toggleSelected={() => setMarkMembers(!markMembers)}
                color="#1E90FF"
                icon={Visibility}
              />
              <FilterListItem
                label="Familles d'accueil"
                isSelected={markHostFamily}
                toggleSelected={() => setMarkHostFamily(!markHostFamily)}
                color="#82CFFF"
                icon={Visibility}
              />
              <FilterListItem
                label="Vétérinaires"
                isSelected={markVeterinary}
                toggleSelected={() => setMarkVeterinary(!markVeterinary)}
                color="#F7C8DF"
                icon={Visibility}
              />
              {accountsStore.isAdmin() && (
                <FilterListItem
                  label="Adoptants"
                  isSelected={markAdopters}
                  toggleSelected={() => setMarkAdopters(!markAdopters)}
                  color="#FCB402"
                  icon={Visibility}
                />
              )}
            </FilterList>
          </Box>
        </OverviewFilterSection>
      </Box>
    )
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        minHeight: 0,
        padding: 0,
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        marginBottom: 0,
        maxHeight: '100%',
      }}
    >
      <Card
        sx={{
          ...spacingItem,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, mb: 1.5 }}>
            <Autocomplete
              id="search-autocomplete"
              defaultValue={selectedMarker}
              options={[...memberMarkers, ...hostFamilyMarkers, ...veterinaryMarkers, ...adopterMarkers]
                .filter(Boolean)
                .sort((a, b) => (a?.title || '').localeCompare(b?.title || ''))}
              getOptionLabel={(option) => option.title}
              renderOption={(props, option) => (
                <li {...props} key={`${option.id} - ${option.title}`}>
                  {option.title}
                </li>
              )}
              style={{ width: '100%' }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Rechercher un marqueur" sx={searchFieldSx} />
              )}
              popupIcon={<Search />}
              onChange={handleSelectMarker}
              sx={{
                [`& .${autocompleteClasses.popupIndicator}`]: {
                  transform: 'none',
                },
              }}
            />
            <Button
              variant="outlined"
              startIcon={
                <Box component="span" sx={overviewFilterCountBadgeSx}>
                  {activeFiltersCount}
                </Box>
              }
              onClick={() => setIsFiltersOpen(true)}
              sx={{ ...overviewActionButtonSx, flexShrink: 0 }}
            >
              Filtres
            </Button>
            {activeFiltersCount > 0 && (
              <Button variant="text" onClick={resetFilters} sx={overviewResetFiltersSx}>
                Reset les filtres
              </Button>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            flex: 1,
            minHeight: { xs: 360, md: 520 },
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <MapOfFrance
            memberMarkers={markMembers ? memberMarkers : []}
            hostFamilyMarkers={markHostFamily ? hostFamilyMarkers : []}
            veterinaryMarkers={markVeterinary ? veterinaryMarkers : []}
            adopterMarkers={markAdopters ? adopterMarkers : []}
            selectedMarker={selectedMarker}
          />
        </Box>

        <OverviewFilterDialog open={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} title="Filtres carte">
          <FiltersBar />
        </OverviewFilterDialog>
      </Card>
    </Box>
  )
}
